import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  addDoc,
  updateDoc,
  getDoc,
  Timestamp,
  Unsubscribe,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from './firebase';
import type { CRMUser, ProposalOpsReviewComment, ProposalOpsReviewMeta, ProposalOpsReviewNodeType } from '../types';
import { getProjectManagers, getSalesReps } from './employeeDesignations';

export { getProjectManagers, getSalesReps as getSalesRepCandidates };

const OPS_REVIEW_META_DOC_ID = 'meta';

function parseTimestamp(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  return undefined;
}

function parseOpsReviewMeta(data: Record<string, unknown> | undefined): ProposalOpsReviewMeta | null {
  if (!data) return null;
  return {
    status: (data.status as ProposalOpsReviewMeta['status']) || 'none',
    requestedAt: parseTimestamp(data.requestedAt),
    requestedBy: data.requestedBy as string | undefined,
    requestedByName: data.requestedByName as string | undefined,
    reviewerUserId: data.reviewerUserId as string | undefined,
    reviewerName: data.reviewerName as string | undefined,
    salesRepUserId: data.salesRepUserId as string | undefined,
    salesRepName: data.salesRepName as string | undefined,
    approvedAt: parseTimestamp(data.approvedAt),
    approvedBy: data.approvedBy as string | undefined,
    approvedByName: data.approvedByName as string | undefined,
    approvedVersionId: data.approvedVersionId as string | undefined,
    requestMessage: data.requestMessage as string | undefined,
    withdrawnAt: parseTimestamp(data.withdrawnAt),
    withdrawnBy: data.withdrawnBy as string | undefined,
    withdrawnByName: data.withdrawnByName as string | undefined,
  };
}

function parseOpsReviewComment(id: string, data: Record<string, unknown>): ProposalOpsReviewComment {
  return {
    id,
    nodeSetId: (data.nodeSetId as string) || '',
    nodeSetName: (data.nodeSetName as string) || '',
    nodeType: data.nodeType as ProposalOpsReviewComment['nodeType'],
    nodeLabel: (data.nodeLabel as string) || undefined,
    message: (data.message as string) || '',
    authorId: (data.authorId as string) || '',
    authorName: (data.authorName as string) || '',
    createdAt: parseTimestamp(data.createdAt) || new Date(),
  };
}

export function subscribeOpsReviewMeta(
  dealId: string,
  callback: (meta: ProposalOpsReviewMeta | null) => void
): Unsubscribe {
  const ref = doc(db, 'proposalBuilders', dealId, 'opsReview', OPS_REVIEW_META_DOC_ID);
  return onSnapshot(
    ref,
    snap => {
      callback(snap.exists() ? parseOpsReviewMeta(snap.data() as Record<string, unknown>) : null);
    },
    err => {
      console.error('[subscribeOpsReviewMeta]', err);
      callback(null);
    }
  );
}

export function subscribeOpsReviewComments(
  dealId: string,
  callback: (comments: ProposalOpsReviewComment[]) => void
): Unsubscribe {
  const q = query(
    collection(db, 'proposalBuilders', dealId, 'opsReviewComments'),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(
    q,
    snap => {
      const comments = snap.docs.map(d =>
        parseOpsReviewComment(d.id, d.data() as Record<string, unknown>)
      );
      callback(comments);
    },
    err => {
      console.error('[subscribeOpsReviewComments]', err);
      callback([]);
    }
  );
}

export interface RequestOpsReviewParams {
  dealId: string;
  reviewerUserId: string;
  reviewerName: string;
  salesRepUserId: string;
  salesRepName: string;
  requestedBy: string;
  requestedByName: string;
  requestMessage?: string;
}

export async function requestOpsReview(params: RequestOpsReviewParams): Promise<void> {
  const {
    dealId,
    reviewerUserId,
    reviewerName,
    salesRepUserId,
    salesRepName,
    requestedBy,
    requestedByName,
    requestMessage,
  } = params;

  const ref = doc(db, 'proposalBuilders', dealId, 'opsReview', OPS_REVIEW_META_DOC_ID);
  const payload: Record<string, unknown> = {
    status: 'pending',
    requestedAt: Timestamp.now(),
    requestedBy,
    requestedByName,
    reviewerUserId,
    reviewerName,
    salesRepUserId,
    salesRepName,
    approvedAt: null,
    approvedBy: null,
    approvedByName: null,
    approvedVersionId: null,
  };
  if (requestMessage?.trim()) {
    payload.requestMessage = requestMessage.trim();
  }
  await setDoc(ref, payload, { merge: true });
}

export interface AddOpsReviewCommentParams {
  dealId: string;
  nodeSetId: string;
  nodeSetName: string;
  nodeType?: ProposalOpsReviewNodeType;
  nodeLabel?: string;
  message: string;
  authorId: string;
  authorName: string;
}

export async function addOpsReviewComment(params: AddOpsReviewCommentParams): Promise<void> {
  const { dealId, nodeSetId, nodeSetName, nodeType, nodeLabel, message, authorId, authorName } = params;
  const payload: Record<string, unknown> = {
    nodeSetId,
    nodeSetName,
    message: message.trim(),
    authorId,
    authorName,
    createdAt: Timestamp.now(),
  };
  if (nodeType) payload.nodeType = nodeType;
  if (nodeLabel) payload.nodeLabel = nodeLabel;
  await addDoc(collection(db, 'proposalBuilders', dealId, 'opsReviewComments'), payload);
}

export interface ApproveOpsReviewParams {
  dealId: string;
  approvedBy: string;
  approvedByName: string;
  approvedVersionId?: string;
}

export async function approveOpsReview(params: ApproveOpsReviewParams): Promise<void> {
  const { dealId, approvedBy, approvedByName, approvedVersionId } = params;
  const ref = doc(db, 'proposalBuilders', dealId, 'opsReview', OPS_REVIEW_META_DOC_ID);
  await updateDoc(ref, {
    status: 'approved',
    approvedAt: Timestamp.now(),
    approvedBy,
    approvedByName,
    ...(approvedVersionId ? { approvedVersionId } : {}),
  });
}

export interface WithdrawOpsReviewParams {
  dealId: string;
  withdrawnBy: string;
  withdrawnByName: string;
}

export async function withdrawOpsReview(params: WithdrawOpsReviewParams): Promise<void> {
  const { dealId, withdrawnBy, withdrawnByName } = params;
  const ref = doc(db, 'proposalBuilders', dealId, 'opsReview', OPS_REVIEW_META_DOC_ID);
  await updateDoc(ref, {
    status: 'none',
    withdrawnAt: Timestamp.now(),
    withdrawnBy,
    withdrawnByName,
  });
}

/** Clears OPS approval when the proposal is edited after sign-off (approval no longer valid). */
export async function revokeOpsReviewApproval(dealId: string): Promise<boolean> {
  const ref = doc(db, 'proposalBuilders', dealId, 'opsReview', OPS_REVIEW_META_DOC_ID);
  const snap = await getDoc(ref);
  if (!snap.exists() || (snap.data()?.status as string) !== 'approved') {
    return false;
  }
  await updateDoc(ref, {
    status: 'none',
    approvedAt: null,
    approvedBy: null,
    approvedByName: null,
    approvedVersionId: null,
  });
  return true;
}

export function canWithdrawOpsReview(
  meta: ProposalOpsReviewMeta | null,
  userId: string | undefined,
  userRole: string | undefined
): boolean {
  if (!meta || meta.status !== 'pending' || !userId) return false;
  if (userRole === 'admin') return true;
  return meta.requestedBy === userId;
}

export function hasOpsReviewActivity(
  meta: ProposalOpsReviewMeta | null,
  commentCount: number
): boolean {
  if (commentCount > 0) return true;
  if (!meta) return false;
  if (meta.status === 'pending' || meta.status === 'approved') return true;
  return !!(meta.requestedAt || meta.withdrawnAt || meta.approvedAt);
}

export function canApproveOpsReview(
  meta: ProposalOpsReviewMeta | null,
  userId: string | undefined,
  userRole: string | undefined
): boolean {
  if (!meta || meta.status !== 'pending' || !userId) return false;
  if (userRole === 'admin') return true;
  return meta.reviewerUserId === userId;
}

export function canCommentOnOpsReview(
  meta: ProposalOpsReviewMeta | null,
  userId: string | undefined,
  userRole: string | undefined
): boolean {
  if (!meta || meta.status !== 'pending' || !userId) return false;
  if (userRole === 'admin') return true;
  return meta.reviewerUserId === userId;
}

/** Comments for a structure card/modal (falls back to name if ids drifted). */
export function getCommentsForStructure(
  comments: ProposalOpsReviewComment[],
  nodeSetId: string,
  nodeSetName: string
): ProposalOpsReviewComment[] {
  const byId = comments.filter(c => c.nodeSetId === nodeSetId);
  if (byId.length > 0) return byId;
  return comments.filter(c => c.nodeSetName === nodeSetName);
}

export function countCommentsForNodeSet(
  comments: ProposalOpsReviewComment[],
  nodeSetId: string,
  nodeSetName?: string
): number {
  if (nodeSetName) {
    return getCommentsForStructure(comments, nodeSetId, nodeSetName).length;
  }
  return comments.filter(c => c.nodeSetId === nodeSetId).length;
}

export function shouldShowOpsCommentsOnStructure(
  opsReviewPending: boolean,
  structureCommentCount: number,
  canComment: boolean,
  opsReviewApproved = false
): boolean {
  if (canComment) return true;
  if (structureCommentCount > 0) return true;
  if (opsReviewPending) return true;
  if (opsReviewApproved && structureCommentCount > 0) return true;
  return false;
}

export function shouldShowOpsCommentsOnSection(
  opsReviewPending: boolean,
  sectionCommentCount: number,
  canComment: boolean,
  opsReviewApproved = false,
  structureCommentCount = 0
): boolean {
  if (canComment) return true;
  if (sectionCommentCount > 0) return true;
  if (opsReviewPending) return true;
  if (opsReviewApproved && structureCommentCount > 0) return sectionCommentCount > 0;
  return false;
}

function normalizeCommentNodeType(c: ProposalOpsReviewComment): ProposalOpsReviewNodeType {
  return c.nodeType || 'structure';
}

function filterCommentsForNodeType(
  comments: ProposalOpsReviewComment[],
  nodeType: ProposalOpsReviewNodeType = 'structure'
): ProposalOpsReviewComment[] {
  return comments.filter(c => {
    const t = normalizeCommentNodeType(c);
    if (nodeType === 'structure') return t === 'structure';
    return t === nodeType;
  });
}

/** Comments for a structure and optional cost section (`structure` = general / legacy). */
export function filterCommentsForNode(
  comments: ProposalOpsReviewComment[],
  nodeSetId: string,
  nodeType: ProposalOpsReviewNodeType = 'structure',
  nodeSetName?: string
): ProposalOpsReviewComment[] {
  const structureComments = nodeSetName
    ? getCommentsForStructure(comments, nodeSetId, nodeSetName)
    : comments.filter(c => c.nodeSetId === nodeSetId);
  return filterCommentsForNodeType(structureComments, nodeType);
}

/** Filter pre-scoped structure comments by cost section only. */
export function filterStructureCommentsByNodeType(
  structureComments: ProposalOpsReviewComment[],
  nodeType: ProposalOpsReviewNodeType = 'structure'
): ProposalOpsReviewComment[] {
  return filterCommentsForNodeType(structureComments, nodeType);
}

export function countCommentsForNode(
  comments: ProposalOpsReviewComment[],
  nodeSetId: string,
  nodeType: ProposalOpsReviewNodeType = 'structure'
): number {
  return filterCommentsForNode(comments, nodeSetId, nodeType).length;
}

export function getOpsReviewNodeLabel(c: ProposalOpsReviewComment): string {
  if (c.nodeLabel) return c.nodeLabel;
  if (!c.nodeType || c.nodeType === 'structure') return 'General';
  const labels: Record<ProposalOpsReviewNodeType, string> = {
    structure: 'General',
    design: 'Design',
    timber: 'Timbers',
    materials: 'Materials',
    production: 'Production',
    freight: 'Freight',
    overhead: 'Overhead',
    install: 'Installation',
    travel: 'Travel',
  };
  return labels[c.nodeType] || c.nodeType;
}
